import ReflectionAttribute from "csi/reflection/ReflectionAttribute";
import ReflectionClass from "csi/reflection/ReflectionClass";
import ReflectionDomainClass from "csi/reflection/ReflectionDomainClass";
import ReflectionDomainRelation from "csi/reflection/ReflectionDomainRelation";
import { ATTR_MASTER, TYPE_LOOKUP_ITEM, getClass, getDomainClass, getDomainClassForReflectionType, getDomainType, getLookupItemOptionalAttributesBySubtype, getRelationToSecondary, isRelationFromSecondary, isRelationToSecondary } from "csi/reflection/reflection";
import { IReflectionDomainAttribute } from "csi/reflection/reflectionTypes";
import reflectionHints from "csi/reflectionHints";
import { arrayLast } from "csi/utility/arrayUtility";
import { isDefined, isNullOrUndefined } from "csi/utility/coreUtility";
import { PATH_OP_CHILD, PATH_OP_INDEX, PATH_OP_PARENT, pathCreate } from "csi/utility/pathUtility";
import xml from "csi/xml";
import _ from "lodash";
import { ATTR_PARENT, C8AttributeId, C8NodeType } from "src/c8-platform/type/node";
import PathHints from "./PathHints";
import { ExtendedPathStep, createExtendedPathStep, parseExtendedPath, parsePathSteps, registerExtendedPathClass } from "./extendedPathService";

/**
 * Calculate standard path starting from the step with the given index
 * @param bOmitIndex - set to true to get path without Index steps
 */
function _getStandardPathFromStep(steps: ExtendedPathStep[], index: number, bOmitIndex?: boolean): string {
	const { length } = steps;
	let path = "";
	let step: ExtendedPathStep;

	while (index < length) {
		step = steps[index++];
		path = pathCreate(path, (step.isParent ? PATH_OP_PARENT : PATH_OP_CHILD), step.a);

		if (!bOmitIndex && isDefined(step.key)) {
			path = pathCreate(path, PATH_OP_INDEX, step.key);
		}
	}

	return path;
}

/**
 * Return target class for given path from start type with the provided path hints
 */
function _getPathHintTarget(startType: C8NodeType, path: string, pathHints: PathHints): ReflectionClass {
	let rcTarget: ReflectionClass;

	getClass(startType).traverse(true, false, ({ type }) => {
		const targetType = pathHints.getTarget(type, path);
		if (targetType) {
			rcTarget = getClass(targetType);
			return true;
		}
	});

	return rcTarget;
}

/**
 * Return true if two steps are the same, except possibly the type which could be different just
 * because of a difference in a base/derived class. If not ignoring type, then also check to see if first class inherits from second
 */
function _isAlongStep(s1: ExtendedPathStep, s2: ExtendedPathStep, ignoreType?: boolean): boolean {
	return (s1.a === s2.a) &&
		((s1.key === s2.key) || (isDefined(s1.key) && isNullOrUndefined(s2.key))) &&
		(s1.isParent === s2.isParent) &&
		(ignoreType || getClass(s1.c).isDerivedFrom(s2.c));
}

/**
 * Return true if first step of path stays within the same domain class
 */
function _isWithinDomain(ep: ExtendedPath): boolean {
	if (!ep.depth) {
		return false;
	}

	const startType = ep.getStartType();
	const firstStep = ep.steps[0];

	return isRelationToSecondary(startType, firstStep.a, firstStep.c) || isRelationFromSecondary(startType, firstStep.a, firstStep.c);
}

class ExtendedPath {
	/** setting to indicate if path should care about types */
	readonly ignoreTypes: boolean;

	/** path hints to aid in identifying destination classes along path */
	readonly pathHints: PathHints;

	/** steps in path */
	readonly steps: ExtendedPathStep[];

	/** string representation of typed path (generated on-demand) */
	#extendedPath: string;

	/** string representation of non-typed path (generated on-demand) */
	#standardPath: string;

	/** class type at which path starts */
	#startType: C8NodeType;

	/**
	 * Create ExtendedPath with optional start type and hints. If the start type is not specified, then that normally means
	 * you are parsing an existing path rather than constructing a new path step by step.
	 */
	constructor(startClass?: C8NodeType | ReflectionClass, pathHints?: PathHints, ignoreTypes?: boolean, steps?: ExtendedPathStep[]) {
		const startType = (typeof startClass === "string") ? startClass : startClass?.type;

		this.ignoreTypes = !!ignoreTypes;
		this.pathHints = pathHints || PathHints.globalHints;
		this.#startType = startType;
		this.steps = steps || [];
	}

	/**
	 * Insert step at the beginning of the path
	 */
	addStartStep(startType: C8NodeType, attributeId: C8AttributeId, stepIndex?: number | string, isParentStep?: boolean): this {
		const targetType = this.startType;
		this.#extendedPath = null;
		this.#standardPath = null;
		this.#startType = startType;
		this.steps.unshift(createExtendedPathStep(attributeId, targetType, stepIndex, isParentStep));
		return this;
	}

	/**
	 * Return true if path backtracks along bi-directional relationship
	 */
	backtracks(): boolean {
		const { depth, steps } = this;
		let lastRelation: ReflectionAttribute;
		let compositionCount = 0;

		for (let i = 0; i < depth; ++i) {
			if (steps[i].isParent) {
				lastRelation = null;
				compositionCount = 0;
			} else {
				const relation = this.getStepRelation(i);
				if (relation) {
					const { attributeId, bidirectionalRelation, isComposite, isSingle } = relation;

					if (compositionCount && (attributeId === xml.ATTR_PARENT)) {
						// when we have a matrix path that goes through multiple composite relationships, it is ok to backtrack
						// so long as the number of times is less than the number of composite relationships
						// Ok: (Style)Child:ProductSourcing(StyleSourcing)/Child:ProductSources(ProductSource)/Child:Samples(Sample)/Child:__Parent__(ProductSource)
						// Not Ok: (Style)Child:ProductSourcing(StyleSourcing)/Child:ProductSources(ProductSource)/Child:Samples(Sample)/Child:__Parent__(ProductSource)/Child:__Parent__(StyleSourcing)
						--compositionCount;

						if (!compositionCount) {
							// console.log("Backtracks composition: ", this.toString());
							return true;
						}
					} else {
						// if not backing out of composition, then test for backtrack on any other bidrectional relation
						if (bidirectionalRelation && (lastRelation?.bidirectionalRelation?.attributeId === attributeId)) {
							// console.log("Backtracks: ", this.toString());
							return true;
						}

						// check for common master-revision backtracking
						if (lastRelation?.isRelationToRevision && ((attributeId === xml.ATTR_PARENT) || (attributeId === ATTR_MASTER))) {
							// console.log("Backtracks: ", this.toString());
							return true;
						}

						// only track non-singular compositions that would form matrix (Child:ProductSourcing should not count)
						if (isComposite && !isSingle) {
							++compositionCount;
						} else {
							compositionCount = 0;
						}
					}
				} else {
					compositionCount = 0;
				}

				lastRelation = relation;
			}
		}

		return false;
	}

	/**
	 * Clear keys for all steps
	 */
	clearKeys(): void {
		this.steps.forEach((step) => {
			step.key = null;
		});
	}

	/**
	 * Clear keys for all steps; used in cases when you are only interested in the classes along the path (PathHints)
	 */
	clearStepKeys(): ExtendedPath {
		const hasNonNullKey = this.steps.some((step) => isDefined(step.key));
		let ep: ExtendedPath = this;

		if (hasNonNullKey) {
			ep = this.clone();
			ep.clearKeys();
		}

		return ep;
	}

	/**
	 * Clone the extended path object
	 */
	clone(): ExtendedPath {
		const { ignoreTypes, pathHints, startType, steps } = this;
		const newSteps = steps.map((step) => createExtendedPathStep(step.a, step.c, step.key, step.isParent));
		return new ExtendedPath(startType, pathHints, ignoreTypes, newSteps);
	}

	/**
	 * Compare another extendedPath with this path
	 */
	equals(ep: ExtendedPath): boolean {
		return !!ep && (this.startType == ep.startType) && _.isEqual(this.steps, ep.steps);
	}

	/**
	 * Return number of steps in the path
	 */
	get depth(): number {
		return this.steps.length;
	}

	/**
	 * Return path that leads to domain
	 */
	get domainPath(): ExtendedPath {
		return this.isPathToSecondary ? this.clone().resolveToDomain() : this;
	}

	/**
	 * Returns the reflection class at the end of the path
	 */
	get endClass(): ReflectionClass {
		return getClass(this.endType);
	}

	getEndClass(): ReflectionClass {
		return this.endClass;
	}

	/**
	 * Returns the domain class at the end of the path
	 */
	get endDomainClass(): ReflectionDomainClass | undefined {
		return getDomainClass(this.endDomainType);
	}

	/**
	 * Return the domain relation at end of the path if it exists (not coming from abstract class)
	 */
	get endDomainRelation(): IReflectionDomainAttribute | undefined {
		const { endRelationId, previousDomainClass } = this;
		return previousDomainClass?.getDomainAttribute(endRelationId);
	}

	/**
	 * Returns the domain type at the end of the path, which may not be the same as the end type if that is a secondary class.
	 */
	get endDomainType(): C8NodeType {
		const { endRelationId, endType, previousType } = this;
		let domainType = getDomainType(endType);

		if (!domainType && previousType && isRelationToSecondary(previousType, endRelationId, endType)) {
			// path could end in shared secondary class, such as IssueLog or DocumentContainer; see if previous step in path
			// is the domain type
			domainType = previousType;
		}

		return domainType;
	}

	/**
	 * Return the relation to the class at the end of the path
	 */
	get endRelation(): ReflectionAttribute | null {
		const { depth } = this;
		return depth ? this.getStepRelation(depth - 1) : null;
	}

	/**
	 * Return the id of the relation to the class at the end of the path
	 */
	get endRelationId(): C8AttributeId | undefined {
		return this.lastStep?.a;
	}

	/**
	 * Return node type at the end of the path
	 */
	get endType(): C8NodeType {
		const { lastStep, startType } = this;
		return lastStep?.c || startType;
	}

	getEndType(): C8NodeType {
		return this.endType;
	}

	/**
	 * Return true if end class is disabled
	 */
	get isDisabled(): boolean {
		return !!this.endClass?.disabled;
	}

	/**
	 * Return true if path is omitted according to given path hints
	 */
	get isOmitted(): boolean {
		return this.pathHints.isPathHidden(this);
	}

	/**
	 * Return true if path ends at a secondary class
	 */
	get isPathToSecondary(): boolean {
		return !!this.endClass?.isSecondary;
	}

	/**
	 * Return last step in the path
	 */
	get lastStep(): ExtendedPathStep | undefined {
		return arrayLast(this.steps);
	}

	/**
	 * Return map of optional attributes for last step in path
	 * NOTE: this currently only applies for LookupItem which is the only relation in model
	 * that references a subtype and has optional attribute configuration per subtype
	 */
	get optionalAttributesMap(): Record<C8AttributeId, boolean> | undefined {
		const { endRelation, endType } = this;

		if (endRelation && (endType === TYPE_LOOKUP_ITEM)) {
			return getLookupItemOptionalAttributesBySubtype(endRelation.subtypeName);
		}
	}

	/**
	 * Returns the domain class at the end of the path
	 */
	get previousDomainClass(): ReflectionDomainClass | undefined {
		return getDomainClass(this.previousType);
	}

	/**
	 * Return the name of the class previous to last class on the path
	 */
	get previousType(): C8NodeType | null {
		const { depth, startType, steps } = this;
		if (depth) {
			return (depth > 1) ? steps[depth - 2].c : startType;
		}

		return null;
	}

	/**
	 * Return array of relations Ids
	 */
	get relationSteps(): C8AttributeId[] {
		return this.steps.map(({ a }) => a);
	}

	/**
	 * Creates (if not already created) and return standard path
	 */
	get standardPath(): string {
		if (isNullOrUndefined(this.#standardPath)) {
			this.#standardPath = this.getStandardPathFromStep(0);
		}
		return this.#standardPath;
	}

	/**
	 * Return path containing only single steps if this path starts with at least one
	 */
	get startingSingularPath(): ExtendedPath | undefined {
		const { depth, startClass, steps } = this;

		if (depth) {
			let rc = startClass;
			let n = 0;

			for (const step of steps) {
				const { a, c, isParent, key } = step;

				if (isParent || isDefined(key)) {
					break;
				}

				const ra = rc.getRelation(a, true, true);
		
				if (!ra || !ra?.isSingle) {
					break;
				}
		
				rc = getClass(c);
				++n;
			}

			if (n && (depth > n)) {
				const ep = this.clone();
				ep.popSteps(depth - n, true);
				return ep;
			}
		}
	}

	/**
	 * Return standard (untyped) path
	 */
	getStandardPath(): string {
		return this.standardPath;
	}

	/**
	 * Calculate standard path starting from the step with the given index
	 * @param bOmitIndex - set to true to get path without Index steps
	 */
	getStandardPathFromStep(index: number, bOmitIndex?: boolean): string {
		return _getStandardPathFromStep(this.steps, index, bOmitIndex);
	}

	/**
	 * Returns the reflection class at the start of the path
	 */
	get startClass(): ReflectionClass {
		return getClass(this.startType);
	}

	/**
	 * Returns the domain class at the start of the path, which may not be the same as the start class if that is a secondary class.
	 */
	get startDomainClass(): ReflectionDomainClass | undefined {
		return getDomainClassForReflectionType(this.startType);
	}

	/**
	 * Returns the domain type at the start of the path, which may not be the same as the start type if that is a secondary class.
	 */
	get startDomainType(): C8NodeType {
		return getDomainType(this.startType);
	}

	/**
	 * Return the id of the relation that the path starts with
	 */
	get startRelationId(): C8AttributeId | undefined {
		return this.steps[0]?.a;
	}

	/**
	 * Returns the node type at the start of the path
	 */
	get startType(): C8NodeType {
		return this.#startType;
	}

	getStartType(): C8NodeType {
		return this.startType;
	}

	/**
	 * Return the string representation of the path
	 */
	get value(): string {
		return this.toString();
	}

	/**
	 * Return start type for step with given index
	 */
	getStartTypeFromStep(index: number): C8NodeType {
		return index ? this.steps[index - 1].c : this.startType;
	}

	/**
	 * Return combined path of this path and another
	 * @param forceClone - specify true if you want to ensure the path returned is always a new path rather than possibly the same as this
	 * @throws the path being added may not be valid according to the model in which case an exception will be thrown
	 */
	plus(pathToAdd: string | ExtendedPath, forceClone?: boolean): ExtendedPath {
		if (!pathToAdd) {
			return forceClone ? this.clone() : this;
		}

		const path = this.clone();

		if (typeof pathToAdd === "string") {
			path.addPath(pathToAdd);
		} else {
			path.append(pathToAdd);
		}

		return path;
	}

	/**
	 * Return end of this path that extends on given start path; it is assumed that this path starts with
	 * the given path and is not checked
	 */
	relativeTo = (startPath: ExtendedPath): ExtendedPath => {
		const n = startPath.depth;
		const endPath = this.clone();
		endPath.shiftSteps(n, true);
		return endPath;
	};

	/**
	 * Return string representation of the ExtendedPath
	 */
	toString(): string {
		const { startType, standardPath, steps } = this;

		if (!startType) {
			return standardPath;
		}

		if (isNullOrUndefined(this.#extendedPath)) {
			let path = "(" + startType + ")";

			steps.forEach(({ a, c, isParent, key }, i) => {
				if (i) {
					path += "/";
				}

				path += (isParent ? PATH_OP_PARENT : PATH_OP_CHILD) + ":" + a;

				if (isDefined(key)) {
					path += "[" + key + "]";
				}

				path += "(" + c + ")";
			});

			this.#extendedPath = path;
		}

		return this.#extendedPath;
	}

	/**
	 * Returns the path of child steps through the reflection model. If a parent step is encountered (rare), then the path is truncated.
	 * This function is used to get a version of the path to see if it is configured as hidden, and hidden paths are configured only with child steps.
	 * @return the path will be truncated if it encounters a parent step
	 * @example The following path "Child:A/Child:B/Index:0/Child:C/Parent:D/Child:E" will return "A/B[0]/C".
	 */
	getChildPath(): string {
		const { steps } = this;
		const res = [];
		let i = 0;
		let step: ExtendedPathStep;

		while (step = steps[i++]) {
			const { a, isParent, key } = step;

			// stop traversing when reach parent step
			if (isParent) {
				break;
			}

			res.push(a + (isNullOrUndefined(key) ? "" : ("[" + key + "]")));
		}

		return res.join("/");
	}

	/**
	 * Return path to use for server query, which will be a typed path only if all classes along the path are concrete
	 * and the path is not empty
	 */
	getQueryPath(ignoreIndex?: boolean): string {
		const { lastStep } = this;

		if (ignoreIndex && isDefined(lastStep.key)) {
			return this.clone().setEndIndex()
				.getQueryPath();
		}

		return (!this.depth || this.isAnyClassAbstract || this.endClass?.hasDerivedClasses()) ? this.standardPath : this.toString();
	}

	/**
	 * Return the Reflection Relation for the given step in the path
	 */
	getStepRelation(index: number): ReflectionAttribute | null {
		const step = this.steps[index];
		const rc = getClass(this.getStartTypeFromStep(index));
		return (rc && step) ? rc.getRelation(step.a, true, true) : null;
	}

	/**
	 * Check if the path stays within the same domain objects
	 * Whether it is empty or only traverses model from/to secondary classes.
	 */
	get inSameDomain(): boolean {
		const { depth } = this;

		if (!depth) {
			return true;
		}

		const ep = this.clone();

		while (_isWithinDomain(ep)) {
			ep.shiftSteps(1, true);
		}

		return !ep.depth;
	}

	/**
	 * Inspects classes used in extended path and returns true if any of them are abstract.
	 */
	get isAnyClassAbstract(): boolean {
		const { startClass, steps } = this;

		if (!startClass) {
			return false;
		}

		if (startClass.abstract) {
			return true;
		}

		return !!steps.some((step) => getClass(step.c)?.abstract);
	}

	/**
	 * Append new step at the end of the path; if ignoreTypes is false, then the targetType could be changed from abstract to concrete based on the latest
	 * model or provided path hints
	 * @param {string} attributeId - id of relation from current end of the path to new class which is about to be added
	 * @param {string} targetType - destination type of the relation
	 */
	addRelationStep(attributeId: C8AttributeId, targetType?: C8NodeType, stepIndex?: number | string, isParentStep?: boolean): this {
		const { ignoreTypes, endClass } = this;

		if (!ignoreTypes) {
			// for Child steps, use getAttribute instead of getRelation in case the attribute is just a ref attribute rather than a relation with a destination class
			let relation = isParentStep ?
				endClass.getRelation(attributeId, true, false, isParentStep) :
				endClass.getAttribute(attributeId, true, false, true);

			if (!relation) {
				// check for relations on derived classes
				const relations = endClass.getRelations(false, true, isParentStep, attributeId);

				if (relations.length == 1) {
					relation = relations[0];
				} else if (!isParentStep && !relations.length && !endClass.getAttribute(attributeId, false, true)) {
					// if isParentStep, relation could be null from the initial call to getRelation due to multiple incoming relations of the
					// same name; we could check that here and throw an exception, but that can be caught below

					// support relationships that are not in model, such as those that are simulated for the client (i.e. __SampleSizeChart__)
					// TODO: think about adding such relationships to the model to prevent such client hacks; not sure there is another example than above
					const typeHint = reflectionHints.getTarget(endClass.type, attributeId);
					if (typeHint) {
						targetType = typeHint;
					} else {
						throw new Error("ExtendedPath#addRelationStep - cannot find the relationship for " + endClass.type + "." + attributeId);
					}
				}
			}

			let rcTarget = getClass(targetType);
			const rcDest = relation?.getDestinationClass(endClass);
			const rcHint = this.#getPathHintForStep(attributeId, isParentStep);

			// if reflection model is more concrete than given type, then use model for target
			if (!rcTarget || rcDest?.isDerivedFrom(targetType)) {
				rcTarget = rcDest;
				targetType = rcTarget.type;
			}

			// if hint is more concrete than given type, then use hint for target
			if (!rcTarget || rcHint?.isDerivedFrom(targetType)) {
				rcTarget = rcHint;
				targetType = rcTarget.type;
			}

			if (!rcTarget) {
				throw new Error("ExtendedPath#addRelationStep - no single destination class exists for " + endClass.type + "." + attributeId);
			}
		}

		this.#addRelationStep(attributeId, targetType, stepIndex, isParentStep);

		return this;
	}

	/**
	 * Add step to secondary class; although, if last step already originated from that secondary class, then remove the last step
	 * rather than add another step back to it
	 */
	addSecondaryRelationStep(secondaryId: C8AttributeId, targetType: C8NodeType): this {
		const { lastStep } = this;

		if (lastStep && !lastStep.isParent) {
			const { previousType } = this;
			const targetClass = getClass(targetType);

			if (targetClass.isDerivedFrom(previousType) && isRelationFromSecondary(previousType, lastStep.a, lastStep.c)) {
				this.popSteps(1, true);
				return this;
			}
		}

		this.addRelationStep(secondaryId, targetType);
		return this;
	}

	/**
	 * Add a standard or extended path onto end of the existing path
	 */
	addPath(path: string | ExtendedPath, targetType?: C8NodeType): void {
		const { endClass, pathHints, depth, startClass, steps } = this;
		const ep = (typeof path === "string") ? parseExtendedPath(path, pathHints, true) : path;

		if (!ep?.depth) {
			return;
		}

		// extended path was passed, so just add steps and return
		const {
			depth: newSize,
			startClass: newClass,
			steps: newSteps,
		} = ep;

		if (newClass) {
			if (newClass.isDerivedFrom(endClass)) {
				this.setEndType(newClass.type);
			} else if (!endClass.isDerivedFrom(newClass)) {
				throw new Error(`Cannot add path starting from class that is different from the end class being added to: ${endClass.type}, ${newClass.type}`);
			}

			for (const step of newSteps) {
				this.#addRelationStep(step.a, step.c, step.key, step.isParent);
			}

			return;
		}

		const rcTarget = getClass(targetType);
		let bDerived = false;

		// need to have array containing old and new steps, as well as classes for each
		let allSteps: ExtendedPathStep[];
		let allClasses: ReflectionClass[];

		if (depth) {
			allSteps = steps.concat(newSteps);
			allClasses = [startClass];

			steps.forEach((step) => {
				allClasses.push(getClass(step.c));
			});
		} else {
			allSteps = newSteps;
			allClasses = [endClass];
		}

		type StepResult = { rcStart?: ReflectionClass; rcEnd?: ReflectionClass };

		const onStep = (rcs: ReflectionClass[], index: number): StepResult | null => {
			const rc = arrayLast(rcs);

			if (index >= newSize) {
				return { rcEnd: rc };
			}

			const isLastIndex = (index == newSteps.length - 1);
			const pathStep = newSteps[index];
			const bChild = !pathStep.isParent;
			const id = pathStep.a;
			let result: StepResult = null;

			// get all destination classes from this step
			let rcHint = (isLastIndex && targetType) ? rcTarget : this.#getPathHintForNewSteps(allSteps, rcs);
			let relations: Partial<ReflectionAttribute>[];
			let nr: number;

			// support relationships that are not in model - such as those generated through rollup on custom attributes
			if (!rcHint) {
				const typeHint = reflectionHints.getTarget(rc.type, id, !bChild);

				if (isDefined(typeHint)) {
					rcHint = getClass(typeHint);

					// in some cases a derived class will not use base class relationships and return an empty typeHint string;
					// in such a case, the relationship does not really exist and should return null
					if (!rcHint) {
						return null;
					}
				}
			}

			if (rcHint) {
				const f1 = (): [ReflectionClass] => [rc];
				const f2 = (): [ReflectionClass] => [rcHint];

				relations = [{
					getDestinationClasses: bChild ? f2 : f1,
					getSourceClasses: bChild ? f1 : f2,
				}];
				nr = 1;
			} else {
				// if no hint, then get all destination classes from this step
				relations = rc.getRelations(true, bDerived, !bChild, id);
				nr = relations?.length;
			}

			// now check all classes of relations
			let classes: ReflectionClass[];
			let nc: number;
			let rcCurrent: ReflectionClass;
			let nextStepResult: StepResult;

			for (let r = 0; r < nr; ++r) {
				classes = bChild ? relations[r].getDestinationClasses(rc) : relations[r].getSourceClasses(rc);
				nc = classes.length;

				for (let i = 0; i < nc; ++i) {
					rcCurrent = classes[i];

					// skip to next class if the destination class does not follow the model
					if (isLastIndex && rcTarget && !rcTarget.isDerivedFrom(rcCurrent)) {
						continue;
					}

					if (nextStepResult = onStep(rcs.concat(rcCurrent), index + 1)) {
						if (result) {
							// it's OK to have several possible results if one class is the base class of others
							if ((result.rcEnd != rcCurrent) && result.rcEnd.isDerivedFrom(rcCurrent)) {
								// create a new result from the base class
								result = null;
							} else if (rcCurrent.isDerivedFrom(result.rcEnd)) {
								// ignore a derived class
								continue;
							}
						}

						if (!result) {
							// get the start class from the relationship.
							// It could be different (base or derived) class than the 'rc'
							let rcStart = rc;
							if (bChild) {
								const sourceClasses = relations[r].getSourceClasses(rc);
								if (sourceClasses?.length) {
									rcStart = sourceClasses[0];

									// if the starting class of the relationship is the base class,
									// leave the original 'rc' class as the starting class because
									// it's where the previous relationship is pointing in the object model
									if (rc.isDerivedFrom(rcStart)) {
										rcStart = rc;
									}
								}
							}

							result = {
								rcStart,
								rcEnd: (nextStepResult.rcStart || rcCurrent),
							};

							// if on last step and found destination, then we're done
							if (isLastIndex && rcTarget && (result.rcEnd.isDerivedFrom(rcTarget) || rcTarget.isDerivedFrom(result.rcEnd))) {
								nr = nc = 0;
							}
						} else {
							throw new Error("Cannot evaluate path '" + path + "' from the class '" + endClass.type + "'. At least two destination classes ['" +
								result.rcEnd.type + "','" + nextStepResult.rcEnd.type + "'] are on the step '" + index + "' from the class '" + rc.type + "' with relation id='" + id + "'");
						}
					}
				}
			}

			if (result) {
				// add end class to the step
				pathStep.c = result.rcEnd.type;
			}

			return result;
		};

		let firstStepResult = onStep(allClasses, 0);
		if (!firstStepResult) {
			// now allow derived class relations to be used if the path was not found without them - this appears to be a very rare case
			bDerived = true;
			firstStepResult = onStep(allClasses, 0);
		}

		if (firstStepResult) {
			if (firstStepResult.rcStart) {
				this.setEndType(firstStepResult.rcStart.type);
			}

			newSteps.forEach(({ a, c, isParent, key }) => {
				this.#addRelationStep(a, c, key, isParent);
			});
		} else {
			throw new Error("Cannot evaluate path '" + path + "' from the class '" + endClass.type + "'. Destination class='" + targetType + "'");
		}
	}

	/**
	 * Append one path to another. Throw an error if the start class of the given path does not match the end class of this path
	 */
	append(ep: ExtendedPath): this {
		if (ep?.depth) {
			if (this.endClass != ep.startClass) {
				throw new Error("Cannot append extended path from class '" + ep.startType + "' to a path ending with class '" + this.endType + "'");
			}

			ep.steps.forEach(({ a, c, isParent, key }) => {
				this.addRelationStep(a, c, key, isParent);
			});
		}

		return this;
	}

	/**
	 * If not ignoring types, then also check to see if start type of this path is derived from the start type of the other
	 */
	isAlongPath(ep: ExtendedPath, ignoreStepTypes?: boolean): boolean {
		const rc1 = this.startClass;
		const rc2 = ep.startClass;

		if (!ignoreStepTypes && rc1 && rc2 && !rc1.isDerivedFrom(rc2)) {
			return false;
		}

		return this.#isAlongPathSteps(ep, ignoreStepTypes);
	}

	/**
	 * Determine if path is for a configurable class that has setup and production settings and return
	 * true if the path should be omitted. In configuration views of Subtypes, for example, the depth
	 * would be 1 and so SetupSettings should be used. But in production views of nodes with subtypes, the
	 * depth would be higher.
	 */
	get isOmittedSubtypeSecondary(): boolean {
		const { depth, endClass, endRelationId, endType } = this;
		const isSetup = (endRelationId === "SetupSettings") || (endRelationId === "Config");
		const isProduction = (endRelationId === "Settings") || (endRelationId === "ProductionConfig");

		if ((isSetup && (depth > 1)) || (isProduction && (depth === 1))) {
			// ensure these attributes lead to a configurable class
			return endClass.isDerivedFrom("SubtypeSettings") ||
				endClass.isDerivedFrom("ProductTypeConfiguration") ||
				endClass.isDerivedFrom("ClassSettings") ||
				endClass.isDerivedFrom("FormDefinition") ||
				endType === "LocaleSettings";
		}

		return false;
	}

	/**
	 * Return true if all steps in path are multiplicity 1
	 */
	get isSingle(): boolean {
		const { depth, steps } = this;
		let { startClass } = this;

		if (!depth) {
			return true;
		}

		return steps.every(({ a, c, isParent, key }) => {
			if (isParent) {
				return false;
			}

			const ra = startClass.getRelation(a, true, true);

			if (!ra || (isNullOrUndefined(key) && !ra.isSingle)) {
				return false;
			}

			startClass = getClass(c);
			return true;
		});
	}


	/**
	 * Get all the possible classes that can be at the end of the path.
	 * This will include all possible non-omitted derived classes unless the depth is 0. Then it'll just be the end class itself.
	 */
	get availableEndClasses(): ReflectionClass[] {
		const { endClass, endDomainRelation } = this;

		let derivedClasses = [];

		if (endDomainRelation) {
			derivedClasses = (endDomainRelation as ReflectionDomainRelation).destinations.map((dc) => dc.type);
		} else if (endClass.abstract){
			derivedClasses = endClass.concreteClasses.map((rc) => rc.type);
		} else {
			// class isn't abstract so the only possible end class is the end class itself
			derivedClasses = [endClass.type];
		}

		// use the path hints that are derived from the java files to filter out the classes
		const nonOmittedClasses: ReflectionClass[] = [];
		const clonedPath = this.clone();
		for (const clazzName of derivedClasses){
			clonedPath.setEndType(clazzName);

			if (clonedPath.isOmitted)
				continue;

			const clazz = getClass(clazzName);
			if (!clazz.enabled)
				continue;

			nonOmittedClasses.push(clazz);
		}

		return nonOmittedClasses;
	};

	/**
	 * Remove step(s) from end, returning ExtendedPath with those steps unless bQuiet
	 * @param n - number of steps to delete
	 * @param bQuiet - do not return ExtendedPath, return null
	 */
	popSteps(n: number, bQuiet?: boolean): ExtendedPath | null {
		const { ignoreTypes, pathHints, steps } = this;
		const newSteps = [];

		while (n--) {
			newSteps.unshift(steps.pop());
		}

		this.#standardPath = null;
		this.#extendedPath = null;

		if (bQuiet) {
			return null;
		}

		return new ExtendedPath(this.endType, pathHints, ignoreTypes, newSteps);
	}

	/**
	 * If the path ends at a secondary class from another domain class, then add parent step to reach domain class
	 */
	resolveToDomain(): this {
		const { endDomainType, isPathToSecondary, previousType } = this;

		if (isPathToSecondary) {
			if (previousType === endDomainType) {
				this.popSteps(1, true);
			} else {
				this.addRelationStep(xml.ATTR_PARENT);
			}
		}

		return this;
	}

	/**
	 * Set index on final step
	 * @throws An index can only be added if there is at least one step in the path (depth != 0)
	 */
	setEndIndex(stepIndex?: number | string): this {
		const { lastStep } = this;

		if (!lastStep) {
			throw new Error("ExtendedPath: index cannot be added without steps");
		}

		lastStep.key = stepIndex?.toString();
		this.#standardPath = null;
		this.#extendedPath = null;
		return this;
	}

	/**
	 * Set end type of path - sometimes necessary to change abstract class into concrete class
	 */
	setEndType(endType: C8NodeType): this {
		const { depth, steps } = this;

		if (depth) {
			steps[depth - 1].c = endType;
			this.validateAgainstDomain();
		} else {
			this.#startType = endType;
		}

		this.#extendedPath = null;
		return this;
	}

	/** set starting type of last step in path */
	setPreviousType = (endDomainType: C8NodeType): this => {
		const { depth, steps } = this;
		if (depth >= 2) {
			steps[depth - 2].c = endDomainType;
		} else {
			this.#startType = endDomainType;
		}

		return this;
	};

	/**
	 * Remove step(s) from the front of the path that take you to the next domain class.
	 * @param bQuiet - do not return ExtendedPath, return null
	 * @example
	 * Extended path "(PrimaryClass)Child:Secondary(SecondaryClass)/Child:A(ClassA)/Child:B(ClassB)" will result in path "(ClassA)Child:B(ChildB)".
	 * Extended path "(SecondaryClass)Child:A(ClassA)/Child:B(ClassB)" will result in path "(ClassA)Child:B(ChildB)".
	 * Extended path "(SecondaryClass)Child:__Parent__(PrimaryClass)/Child:C(ClassC)" will result in path (ClassC)".
	 */
	shiftDomain(bQuiet?: boolean): ExtendedPath | null {
		while (_isWithinDomain(this)) {
			this.shiftSteps(1, true);
		}

		return this.shiftSteps(1, bQuiet);
	}

	/**
	 * Remove step(s) from front, returning ExtendedPath with those steps unless bQuiet
	 * @param n - number of steps to delete
	 * @param bQuiet - do not return ExtendedPath, return undefined
	 */
	shiftSteps(n: number, bQuiet?: boolean): ExtendedPath | undefined {
		const { ignoreTypes, pathHints, depth, startType, steps } = this;
		const newSteps = [];

		n = Math.min(n, depth);

		if (n) {
			while (n--) {
				newSteps.push(steps.shift());
			}

			this.#extendedPath = null;
			this.#standardPath = null;
			this.#startType = arrayLast(newSteps).c;
		}

		if (bQuiet) {
			return;
		}

		return new ExtendedPath(startType, pathHints, ignoreTypes, newSteps);
	}

	/**
	 * If the path starts at a secondary class, then reset path to start from its parent domain class.
	 * @return returns starting domain type if known.
	 * @example
	 * Extended path "(SecondaryClass)Child:A(ClassA)" will result in path (PrimaryClass)Child:SecondaryId(SecondaryClass)/Child:A(ClassA)".
	 * Extended path "(SecondaryClass)Child:__Parent__(PrimaryClass)/Child:B(ClassB)" will result in path (PrimaryClass)Child:B(ClassB)".
	 */
	startFromDomainType(): C8NodeType | null {
		const { depth, startDomainType, startType, steps } = this;

		if (!depth) {
			return null;
		}

		const { a, c } = steps[0];
		const bSecondary = isRelationFromSecondary(startType, a, c);
		let primaryType = startDomainType;

		if (bSecondary && !primaryType) {
			primaryType = getDomainType(c);
		}

		if (primaryType && (primaryType !== startType)) {
			if (bSecondary) {
				// first step is parent step to primary class, so just remove that step
				console.assert(getClass(primaryType).isDerivedFrom(c));
				this.shiftSteps(1, true);
			} else if (a == xml.ATTR_PARENT) {
				// if first step exists and is step to parent node, and that is not a step to the primary type, then something is wrong
				console.error("ExtendedPath.startFromDomainType - parent step to wrong parent? " + this.toString());
				return null;
			} else {
				// otherwise, lookup relation id from primary to secondary type and add step to front of path
				const id = getRelationToSecondary(primaryType, startType);
				if (id) {
					this.addStartStep(primaryType, id);
				}
			}
		}

		return primaryType;
	}

	/** adjust classes at end of path to best match domain model */
	validateAgainstDomain = (): this => {
		const { endDomainClass, endDomainType, endRelation, previousType, setPreviousType } = this;

		if (endDomainType && endRelation?.secondary && (previousType !== endDomainType)) {
			// end class is a secondary class and the previous step is its domain primary class
			setPreviousType(endDomainType);
		} else if (endDomainClass && endRelation) {
			const { bidirectionalRelation } = endRelation;

			if (bidirectionalRelation?.isSecondary) {
				// if previous step is from a secondary class, then update the starting type to the secondary class for the end domain class
				// for example, if path is now "(ProductSourcing)Child:__Parent__(Material)", then change start type to MaterialSourcing
				const secondaryClass = endDomainClass.secondaries[bidirectionalRelation.attributeId];
				if (secondaryClass) {
					setPreviousType(secondaryClass.type);
				}
			}
		}

		return this;
	};

	/**
	 * Append new step at the end of the path
	 * @param {string} attributeId - reflection attribute id that will be added to the end of the path
	 * @param {string} [targetType] - reflection class that is the target of the given attribute
	 * @param {string} [stepIndex]
	 * @param {boolean} [isParentStep]
	 * @throws If the extended path is not told to ignore types, then targetType is required
	 */
	#addRelationStep(attributeId: C8AttributeId, targetType?: C8NodeType, stepIndex?: number | string, isParentStep?: boolean): void {
		const { ignoreTypes, steps, validateAgainstDomain } = this;

		if (!targetType && !ignoreTypes) {
			throw new Error("targetType must be specified");
		}

		this.#standardPath = null;
		this.#extendedPath = null;
		steps.push(createExtendedPathStep(attributeId, targetType, stepIndex, isParentStep));

		if (!ignoreTypes) {
			validateAgainstDomain();
		}
	}

	/**
	 * Return target class from path hints if a new step is added to the existing path
	 */
	#getPathHintForStep(attributeId: C8AttributeId, isParentStep: boolean): ReflectionClass {
		// check path hints to see if concrete type is known for path to abstract class - start by looking for whole path, and then continue
		// looking at each "sub-path" (i.e. step 1 through the last step). Index steps are not considered in the path as they do
		// not affect the classes from step to step.
		const { pathHints, depth } = this;
		const pathStep = pathCreate(isParentStep ? PATH_OP_PARENT : PATH_OP_CHILD, attributeId);
		let i = 0;
		let path: string;
		let startType: C8NodeType;
		let rcTarget: ReflectionClass;

		while (!rcTarget && (i <= depth)) {
			startType = this.getStartTypeFromStep(i);
			path = pathCreate(this.getStandardPathFromStep(i, true), pathStep);
			rcTarget = _getPathHintTarget(startType, path, pathHints);
			++i;
		}

		return rcTarget;
	}

	/**
	 * Return target class from path hints for known steps in new path; in this case, the pathSteps represent the full array of steps
	 * in the path, but the pathClasses represent the classes along the path that have so far been determined. The first entry in pathClasses
	 * is the starting class of the path, so pathClasses represent the starting classes of each step in the path.
	 */
	#getPathHintForNewSteps(pathSteps: ExtendedPathStep[], pathClasses: ReflectionClass[]): ReflectionClass {
		const { pathHints } = this;
		const { length } = pathClasses;
		let i = 0;
		let path: string;
		let startType: C8NodeType;
		let rcTarget: ReflectionClass;

		// copy steps as we will modify it
		pathSteps = pathSteps.slice(0, length);

		while (i < length) {
			startType = pathClasses[i].type;
			path = _getStandardPathFromStep(pathSteps, 0);
			rcTarget = _getPathHintTarget(startType, path, pathHints);

			if (rcTarget) {
				return rcTarget;
			}

			++i;
			pathSteps.shift();
		}

		return rcTarget;
	}

	/**
	 * Return true if this path is the same as, or longer than, the given path
	 * @param ignoreStepTypes - if true, then just compare the relationships in each step, and not also the target type of each step
	 */
	#isAlongPathSteps(ep: ExtendedPath, ignoreStepTypes?: boolean): boolean {
		const { depth, steps } = this;

		if (!ep.depth) {
			return true;
		}

		if (ep.depth > depth) {
			return false;
		}

		return ep.steps.every((s2, i) => _isAlongStep(steps[i], s2, ignoreStepTypes));
	}

	/**
	 * Create and return an extended path for given start type and path, with optional path hints to help traverse the model
	 */
	static create(startClass: C8NodeType | ReflectionClass, path?: string, pathHints?: PathHints): ExtendedPath | undefined {
		try {
			const ep = new ExtendedPath(startClass, pathHints);
			ep.addPath(path);
			return ep;
		} catch (ex) {
			// return undefined
		}
	}

	/**
	 * Create and return an untyped extended path
	 */
	static createUntypedPath(path: string): ExtendedPath {
		const steps = parsePathSteps(path);
		return new ExtendedPath(null, null, true, steps);
	}

	/**
	 * Return parent path starting from given class and ending at the first subtyped class encountered
	 */
	static getParentPathToSubtypedClass(startClass: C8NodeType | ReflectionClass): ExtendedPath | undefined {
		const ep = new ExtendedPath(startClass);

		try {
			while (!ep.endClass.isSubtyped) {
				ep.addRelationStep(ATTR_PARENT);
			}

			return ep;
		} catch (ex) {
		}
	}

	/**
	 * Return end class for given start type and path
	 */
	static getPathEndClass(startClass: C8NodeType | ReflectionClass, path?: string, pathHints?: PathHints): ReflectionClass | undefined {
		const rc = (typeof startClass === "string") ? getClass(startClass) : startClass;
		if (!path) {
			return rc;
		}

		const ep = ExtendedPath.create(rc, path, pathHints);
		return ep?.endClass;
	}
}

registerExtendedPathClass(ExtendedPath);

export = ExtendedPath;